/**
 * PM Groessenfinder
 *
 * Empfiehlt eine Groesse aus Brustumfang, Koerpergroesse und Passform-Vorliebe
 * und zeigt, ob diese Groesse in der gerade gewaehlten Farbe lieferbar ist.
 *
 * Bewusst ohne Theme-Imports, damit die Datei unabhaengig vom Modulgraph des
 * Themes bleibt. Die einzige Verbindung zu Horizon ist das Event
 * "variant:update", das der Variant-Picker auf document feuert.
 */
class PmSizeFinder extends HTMLElement {
  static SIZES = ['S', 'M', 'L', 'XL'];

  // Obergrenze Brustumfang in cm fuer normale Passform.
  static UPPER = { S: 90, M: 98, L: 106 };

  connectedCallback() {
    this.result = this.querySelector('[data-sf="result"]');
    if (!this.result) return;

    const placeholderEl = this.result.querySelector('.pm-sf__placeholder');
    this.placeholder = placeholderEl ? placeholderEl.textContent : '';
    this.hasResult = false;

    this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
    if (isNaN(this.sizeIndex)) this.sizeIndex = -1;
    this.productUrl = this.dataset.productUrl || '';

    const payload = this.parsePayload();
    this.variants = payload.variants;
    this.selected = payload.selected;

    this.handleInput = () => this.update();
    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleInput);

    // Der Variant-Picker morpht standardmaessig nur sich selbst, dieser Knoten
    // bleibt also stehen. Die Farbwahl wird darum per Event nachgezogen statt
    // durch ein Neu-Rendern.
    this.handleVariant = (event) => {
      const resource = event.detail && event.detail.resource;
      if (!resource || !Array.isArray(resource.options)) return;
      this.selected = resource.options;
      if (this.hasResult) this.update();
    };
    document.addEventListener('variant:update', this.handleVariant);

    // Im Theme-Editor aufklappen, sobald der Block ausgewaehlt wird. Sonst
    // sieht der Haendler beim Einstellen nur die zugeklappte Zeile.
    // Das Event feuert auf dem Block-Wrapper, der dieses Element enthaelt.
    this.handleBlockSelect = (event) => {
      const target = event.target;
      if (!(target instanceof Node) || !target.contains(this)) return;
      const details = this.closest('details');
      if (details) details.open = true;
    };
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.handleVariant);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  parsePayload() {
    const node = this.querySelector('[data-sf="variants"]');
    if (!node) return { variants: [], selected: [] };
    try {
      const parsed = JSON.parse(node.textContent);
      return {
        variants: Array.isArray(parsed.variants) ? parsed.variants : [],
        selected: Array.isArray(parsed.selected) ? parsed.selected : [],
      };
    } catch (error) {
      console.warn('[pm-size-finder] Variantendaten unlesbar:', error);
      return { variants: [], selected: [] };
    }
  }

  read(name) {
    const checked = this.querySelector('[data-sf="' + name + '"]:checked');
    const el = checked || this.querySelector('[data-sf="' + name + '"]');
    return el ? el.value : '';
  }

  recommend(chest, height, fit) {
    const SIZES = PmSizeFinder.SIZES;
    const UPPER = PmSizeFinder.UPPER;

    let index = SIZES.findIndex((s) => chest <= (UPPER[s] === undefined ? Infinity : UPPER[s]));
    if (index === -1) index = SIZES.length - 1;

    const reasons = [];
    if (fit === 'eng' && index > 0) {
      index -= 1;
      reasons.push('eine Nummer kleiner, weil du es eng magst');
    }
    if (fit === 'locker' && index < SIZES.length - 1) {
      index += 1;
      reasons.push('eine Nummer größer für den lockeren Sitz');
    }
    if (height >= 190 && index < SIZES.length - 1) {
      index += 1;
      reasons.push('eine Nummer größer wegen deiner Körpergröße');
    }
    return { size: SIZES[index], reasons: reasons };
  }

  /**
   * Lieferbarkeit einer Groesse — aber nur fuer die gerade gewaehlten uebrigen
   * Optionen. Ohne diese Einschraenkung wuerde "L" als lieferbar gelten, weil
   * es in Weiss vorraetig ist, waehrend der Kunde gerade Schwarz gewaehlt hat.
   *
   * @returns {boolean|null} null, wenn es zu dieser Groesse keine Variante gibt
   *                         oder das Produkt gar keine Groessenoption hat.
   */
  available(size) {
    if (this.sizeIndex < 0 || !this.variants.length) return null;

    const matching = this.variants.filter((variant) => {
      if (!Array.isArray(variant.o) || variant.o[this.sizeIndex] !== size) return false;
      return variant.o.every((value, i) => {
        if (i === this.sizeIndex) return true;
        // Optionen, zu denen wir keine Auswahl kennen, schraenken nicht ein.
        return this.selected[i] === undefined || value === this.selected[i];
      });
    });

    if (!matching.length) return null;
    return matching.some((variant) => variant.a);
  }

  // Naechstliegende lieferbare Groesse, bevorzugt aufwaerts.
  nextAvailable(size) {
    const order = PmSizeFinder.SIZES;
    const start = order.indexOf(size);
    if (start === -1) return null;

    for (let step = 1; step < order.length; step++) {
      for (const i of [start + step, start - step]) {
        if (i >= 0 && i < order.length && this.available(order[i]) === true) return order[i];
      }
    }
    return null;
  }

  static escape(value) {
    return String(value).replace(/[&<>"']/g, (char) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  showPlaceholder() {
    this.hasResult = false;
    this.result.innerHTML = '<span class="pm-sf__placeholder">' + PmSizeFinder.escape(this.placeholder) + '</span>';
  }

  update() {
    const chest = parseInt(this.read('chest'), 10);
    const height = parseInt(this.read('height'), 10);
    const fit = this.read('fit') || 'normal';

    if (!chest || chest < 70 || chest > 140) {
      this.showPlaceholder();
      return;
    }

    const rec = this.recommend(chest, height, fit);
    const inStock = this.available(rec.size);

    const note = rec.reasons.length
      ? 'Ich gehe ' + rec.reasons.join(' und ') + '.'
      : 'Das ist die normale Passform für deine Maße.';

    let stockLine = '';
    if (inStock === true) {
      stockLine = '<span class="pm-sf__stock pm-sf__stock--in">Auf Lager</span>';
    } else if (inStock === false) {
      const alt = this.nextAvailable(rec.size);
      stockLine = alt
        ? '<span class="pm-sf__stock pm-sf__stock--out">Gerade ausverkauft — ' +
          PmSizeFinder.escape(alt) +
          ' wäre lieferbar</span>'
        : '<span class="pm-sf__stock pm-sf__stock--out">Gerade ausverkauft</span>';
    }

    const cta = this.productUrl
      ? '<a class="button pm-sf__cta" href="' + PmSizeFinder.escape(this.productUrl) + '">Zum Produkt</a>'
      : '';

    this.hasResult = true;
    this.result.innerHTML =
      '<span class="pm-sf__label">Deine Größe</span>' +
      '<span class="pm-sf__size">' +
      PmSizeFinder.escape(rec.size) +
      '</span>' +
      stockLine +
      '<span class="pm-sf__note">' +
      note +
      '</span>' +
      cta;
  }
}

if (!customElements.get('pm-size-finder')) {
  customElements.define('pm-size-finder', PmSizeFinder);
}
